import React, { useState, } from 'react';
import type { TripCategory } from '../types/customer-portal';
import TripCategoryReminderModal from '../../../shared/components/ui/TripCategoryReminderModal';
import './TripCategorySelector.css';

interface TripCategorySelectorProps {
  selectedCategory: TripCategory;
  onCategoryChange: (category: TripCategory) => void;
  showLabel?: boolean; // Optional prop to control label visibility
  transportMode?: 'air' | 'land' | 'sea'; // For Domestic category modal content
}

// Trip category label content mapping
const TRIP_CATEGORY_LABELS: Record<TripCategory, string> = {
  'Domestic': 'Travel insurance for medical emergencies and trip inconveniences across the Philippines.',
  'International': 'Travel Insurance Plan for Asia and Oceania (e.g., Australia, New Zealand, etc.) and worldwide travel including Schengen countries.',
  'Inbound': 'Travel Insurance Plan for inbound travelers to the Philippines or non-Philippine residents or Expatriates traveling to the Philippines only.'
};

const TripCategorySelector: React.FC<TripCategorySelectorProps> = ({
  selectedCategory,
  onCategoryChange,
  showLabel = true, // Default to showing labels
  transportMode
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState<TripCategory>('International');

  // Session storage keys for tracking shown modals
  const getSessionKey = (category: TripCategory) => `tripCategoryModal_${category}_shown`;

  // Check if modal has been shown for this category in current session
  const hasModalBeenShown = (category: TripCategory) => {
    return sessionStorage.getItem(getSessionKey(category)) === 'true';
  };

  // Mark modal as shown for this category in current session
  const markModalAsShown = (category: TripCategory) => {
    sessionStorage.setItem(getSessionKey(category), 'true');
  };

  // Handle category change with modal display logic
  const handleCategoryChange = (category: TripCategory) => {
    // Always call the original onCategoryChange first
    onCategoryChange(category);

    // Check if modal should be shown for this category
    if (!hasModalBeenShown(category)) {
      setModalCategory(category);
      setIsModalOpen(true);
      markModalAsShown(category);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="trip-category-selector">
      <div className="trip-category-tabs">
        <button
          className={`trip-category-tab ${selectedCategory === 'International' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('International')}
          type="button"
        >
          International
        </button>
        <button
          className={`trip-category-tab ${selectedCategory === 'Domestic' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('Domestic')}
          type="button"
        >
          Domestic
        </button>
        {/* <button
          className={`trip-category-tab ${selectedCategory === 'Inbound' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('Inbound')}
          type="button"
        >
          Inbound
        </button> */}
      </div>
      
      {/* Trip Category Label */}
      {showLabel && (
        <div className="trip-category-label">
          <h4 className="trip-category-label-title">{selectedCategory}</h4>
          <p className="trip-category-label-description">
            {TRIP_CATEGORY_LABELS[selectedCategory]}
          </p>
        </div>
      )}

      {/* Trip Category Reminder Modal */}
      <TripCategoryReminderModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        tripCategory={modalCategory}
        transportMode={transportMode}
      />
    </div>
  );
};

export default TripCategorySelector;